import Graph from "../Graph";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import FieldHelp from "../../help/FieldHelp";
import {
  FOCUS_LOSS_PARAMS,
  buildNodeSettingsLink,
} from "../../utils/nodeSettingsLink";
import "../../pages/InsightsTab.css";

function fmtPkr(val) {
  return Number(val || 0).toLocaleString("en-US");
}

/* One pill colour per Category-dropdown value, so a mixed "All Categories" list is
   still readable at a glance. Energy uses the base `.category` (teal) because the
   stylesheet only ships seven modifier classes for eight categories. */
const CATEGORY_PILL_CLASS = {
  "Voltage": "category-blue",
  "Current": "category-violet",
  "Power": "category-orange",
  "Power Factor": "category-green",
  "Energy": "",
  "Frequency": "category-pink",
  "THD Voltage": "category-indigo",
  "THD Current": "category-rose",
};

/* The three components of the General Loss Calculation Framework:
   Total = (WE x PR) + (PV x D) + (MC x PM). All three are listed so the parts
   visibly add up to the total; the ones a detector leaves at zero are dropped. */
const LOSS_COMPONENTS = [
  { key: "equipmentLoss", label: "Equipment", help: "ensight.lossEquipment" },
  { key: "productionLoss", label: "Downtime", help: "ensight.lossDowntime" },
  { key: "maintenanceLoss", label: "Maintenance", help: "ensight.lossMaintenance" },
];

/* Hover help per Category-dropdown value and per insight name (src/help/ensight.js).
   Names come from the backend detectors; an unknown one simply gets no hover card. */
const CATEGORY_HELP_ID = {
  "Voltage": "ensight.catVoltage",
  "Current": "ensight.catCurrent",
  "Power": "ensight.catPower",
  "Power Factor": "ensight.catPowerFactor",
  "Energy": "ensight.catEnergy",
  "Frequency": "ensight.catFrequency",
  "THD Voltage": "ensight.catThdVoltage",
  "THD Current": "ensight.catThdCurrent",
};

const INSIGHT_HELP_ID = {
  "Voltage Sag": "ensight.typeVoltageSag",
  "Voltage Swell": "ensight.typeVoltageSwell",
  "Under Voltage": "ensight.typeUnderVoltage",
  "Over Voltage": "ensight.typeOverVoltage",
  "Voltage Unbalance": "ensight.typeVoltageUnbalance",
  "Transients and Spikes": "ensight.typeTransients",
  "Phase Unbalance": "ensight.typePhaseUnbalance",
  "Phase Loss": "ensight.typePhaseLoss",
  "Leakage Current": "ensight.typeLeakageCurrent",
  "Inrush Current": "ensight.typeInrushCurrent",
  "High Power": "ensight.typeHighPower",
  "Low Power": "ensight.typeLowPower",
  "MDI Above Sanctioned Load": "ensight.typeMdiExceedance",
  "Machine On In Off Time": "ensight.typeOnInOffTime",
  "Machine Off In On Time": "ensight.typeOffInOnTime",
  "Low Power Factor": "ensight.typeLowPf",
  "Power Factor Fluctuation": "ensight.typePfFluctuation",
  "Leading Power Factor": "ensight.typeLeadingPf",
  "High Daily Energy Consumption": "ensight.typeHighDailyEnergy",
  "High Monthly Energy Consumption": "ensight.typeHighMonthlyEnergy",
  "High Daily Energy Produced by Genset": "ensight.typeHighDailyGenset",
  "High Monthly Energy Produced by Genset": "ensight.typeHighMonthlyGenset",
  "Low Daily Energy Produced by Solar PV": "ensight.typeLowDailySolar",
  "Low Monthly Energy Produced by Solar PV": "ensight.typeLowMonthlySolar",
  "Under Frequency": "ensight.typeUnderFrequency",
  "Over Frequency": "ensight.typeOverFrequency",
  "Frequency Fluctuations": "ensight.typeFrequencyFluctuations",
  "High THD Voltage": "ensight.typeHighThdv",
  "Intermittent THD Voltage": "ensight.typeIntermittentThdv",
  "High THD Current": "ensight.typeHighThdi",
  "Intermittent THD Current": "ensight.typeIntermittentThdi",
};

/* The two THD detectors both report category "Power Quality", so the row's own
   `category` cannot tell them apart. `categoryKey` is the label the combined
   endpoint stamps on, and it matches the Category dropdown exactly. */
export function categoryLabel(insight) {
  return insight?.categoryKey || insight?.category || "";
}

function SeverityPill({ severity }) {
  const isCrit = severity === "Critical";
  return (
    <span className={`pill ${isCrit ? "critical" : "warning"}`}>
      <span className="tiny-dot" />
      <FieldHelp id="ensight.severityCol">{severity}</FieldHelp>
    </span>
  );
}

function CategoryPill({ insight }) {
  const label = categoryLabel(insight);
  const modifier = CATEGORY_PILL_CLASS[label] || "";
  return (
    <span className={`category ${modifier}`.trim()}>
      <FieldHelp id={CATEGORY_HELP_ID[label]}>{label}</FieldHelp>
    </span>
  );
}

/* Detail Modal Popup with solid white background and dark backdrop */
function InsightModal({ insight, onClose, onAssignLossParams }) {
  if (!insight) return null;

  const points = insight.chartData || [];
  const lossParts = LOSS_COMPONENTS.filter(({ key }) => Number(insight[key]) > 0);

  return (
    <div className="insights-overlay">
      <div className="detail-backdrop" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="modal-head-row">
            <div>
              <div className="modal-title-row" style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                <CategoryPill insight={insight} />
                <SeverityPill severity={insight.severity} />
              </div>
              <h2><FieldHelp id={INSIGHT_HELP_ID[insight.name]}>{insight.name}</FieldHelp></h2>
              <div className="modal-machine">
                {insight.machine} • <FieldHelp id="ensight.detected">Detected:</FieldHelp> <strong>{insight.detected}</strong>
              </div>
            </div>
            <button className="close-btn" onClick={onClose} aria-label="Close modal">
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div className="modal-body">
          <p style={{ margin: 0, fontSize: "14px", color: "#475569", lineHeight: "1.5" }}>{insight.description}</p>

          {/* Per-machine numbers behind the detection: rated power, sanctioned load,
              wasted kWh. Without this the thresholds are unverifiable from the UI.
              Only the Power and Energy detectors fill it in. */}
          {insight.detail && (
            <p style={{ margin: 0, fontSize: "13px", color: "#0f172a", fontWeight: 600, lineHeight: "1.5" }}>
              {insight.detail}
            </p>
          )}

          {/* Financial Impact Cards */}
          {insight.remedyCost > 0 ? (
            <div className="financial-grid">
              <div className="financial-card">
                <div>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}><FieldHelp id="ensight.loss">Estimated Financial Loss</FieldHelp></div>
                  <div style={{ fontSize: "22px", fontWeight: "800", color: "#dc2626", marginTop: "4px" }}>PKR {fmtPkr(insight.loss)}</div>
                </div>
                <div style={{ fontSize: "12px", textAlign: "right", color: "#475569", lineHeight: "1.6" }}>
                  {lossParts.map(({ key, label, help }) => (
                    <div key={key}>
                      <FieldHelp id={help}>{label}:</FieldHelp> <strong>PKR {fmtPkr(insight[key])}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="financial-card">
                <div>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}><FieldHelp id="ensight.fixCost">Estimated Remedy Cost</FieldHelp></div>
                  <div style={{ fontSize: "22px", fontWeight: "800", color: "#0f766e", marginTop: "4px" }}>PKR {fmtPkr(insight.remedyCost)}</div>
                </div>
                <div style={{ fontSize: "12px", textAlign: "right", color: "#475569", lineHeight: "1.6" }}>
                  {insight.remedyCostLow && insight.remedyCostHigh && (insight.remedyCostLow !== insight.remedyCostHigh) && (
                    <div>
                      <FieldHelp id="ensight.remedyRange">Range:</FieldHelp> <strong>PKR {fmtPkr(insight.remedyCostLow)} – {fmtPkr(insight.remedyCostHigh)}</strong>
                    </div>
                  )}
                  {insight.savings > 0 && (
                    <div style={{ color: "#0f766e" }}>
                      <FieldHelp id="ensight.payback">Payback:</FieldHelp> <strong>~{((insight.remedyCost / insight.savings) * 12).toFixed(1)} mo</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="financial-card">
              <div>
                <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}><FieldHelp id="ensight.loss">Estimated Financial Loss</FieldHelp></div>
                <div style={{ fontSize: "22px", fontWeight: "800", color: "#dc2626", marginTop: "4px" }}>PKR {fmtPkr(insight.loss)}</div>
              </div>
              <div style={{ fontSize: "12px", textAlign: "right", color: "#475569", lineHeight: "1.6" }}>
                {lossParts.map(({ key, label, help }) => (
                  <div key={key}>
                    <FieldHelp id={help}>{label}:</FieldHelp> <strong>PKR {fmtPkr(insight[key])}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* The figure above is only as good as the three inputs behind it, and
              the backend falls back to generic defaults for any the client has
              not entered. Offer the jump to them rather than presenting the
              number as settled. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "-4px",
              fontSize: "12px",
              color: "#64748b",
              lineHeight: "1.5",
            }}
          >
            <span>
              Priced from this machine&apos;s Worth of Equipment, Production
              Value / Hr and Maintenance Cost. Defaults are used for any that
              are not set.
            </span>
            <button
              type="button"
              onClick={() => onAssignLossParams?.(insight)}
              disabled={!onAssignLossParams}
              title={`Open Node Settings for ${insight.machine}`}
              style={{
                background: "#fffbeb",
                border: "1px dashed #f59e0b",
                borderRadius: "6px",
                padding: "6px 12px",
                color: "#b45309",
                fontSize: "12px",
                fontWeight: 700,
                cursor: onAssignLossParams ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              Set WE / PV / MC
            </button>
          </div>

          {/* The figure above is only as good as the three inputs behind it, and
              the backend falls back to generic defaults for any the client has
              not entered. Offer the jump to them rather than presenting the
              number as settled. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "-4px",
              fontSize: "12px",
              color: "#64748b",
              lineHeight: "1.5",
            }}
          >
            <span>
              Priced from this machine&apos;s Worth of Equipment, Production
              Value / Hr and Maintenance Cost. Defaults are used for any that
              are not set.
            </span>
            <button
              type="button"
              onClick={() => onAssignLossParams?.(insight)}
              disabled={!onAssignLossParams}
              title={`Open Node Settings for ${insight.machine}`}
              style={{
                background: "#fffbeb",
                border: "1px dashed #f59e0b",
                borderRadius: "6px",
                padding: "6px 12px",
                color: "#b45309",
                fontSize: "12px",
                fontWeight: 700,
                cursor: onAssignLossParams ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              Set WE / PV / MC
            </button>
          </div>

          {/* Visual Trend Chart */}
          {points.length > 0 && (() => {
            const hasThreshold = points.some((p) => p.threshold !== undefined && p.threshold !== null);
            const datasets = [
              {
                label: insight.chartParam || "Measured Value",
                data: points.map((p) => p.value),
                borderColor: "#1BA08E",
                backgroundColor: "rgba(27, 160, 142, 0.12)",
                fill: true,
                tension: 0.3,
                pointRadius: 3,
                borderWidth: 2,
              },
            ];

            if (hasThreshold) {
              datasets.push({
                label: "Threshold Limit",
                data: points.map((p) => p.threshold),
                borderColor: "#ef4444",
                borderDash: [5, 4],
                pointRadius: 0,
                fill: false,
                borderWidth: 2,
              });
            }

            const unitSuffix = insight.chartUnit ? ` ${insight.chartUnit}` : "";
            const currentThreshold = insight.severity === "Critical" && insight.criticalThreshold
              ? insight.criticalThreshold
              : insight.warningThreshold;

            return (
              <div className="section">
                <p className="section-title">
                  <FieldHelp id="ensight.trendChart">
                    {insight.chartParam}{insight.chartUnit ? ` (${insight.chartUnit})` : ""}
                    {currentThreshold ? ` • Threshold: ${currentThreshold}` : ""}
                  </FieldHelp>
                </p>
                <div style={{ background: "#fff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ height: "220px", width: "100%", marginTop: "10px" }}>
                    <Graph
                      labels={points.map((p, i) => p.time || p.timestamp || `Point ${i + 1}`)}
                      datasets={datasets}
                      valueFormatter={(v) =>
                        Number.isFinite(v)
                          ? `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 3 })}${unitSuffix}`
                          : `${v || ""}${unitSuffix}`
                      }
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: hasThreshold,
                            position: "top",
                            align: "end",
                            labels: {
                              boxWidth: 12,
                              font: { size: 11, weight: 600 },
                            },
                          },
                        },
                        scales: {
                          x: { ticks: { maxTicksLimit: 8 } },
                          y: {
                            title: { display: Boolean(insight.chartUnit), text: insight.chartUnit || "" },
                            beginAtZero: false,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Likely Root Causes */}
          <div className="section">
            <p className="section-title"><FieldHelp id="ensight.rootCauses">Likely Root Causes</FieldHelp></p>
            <ul className="bullet-list">
              {insight.reasons?.map((r, i) => (
                <li key={i} className="bullet-item">
                  <span className="reason-bullet" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recommended Fixes */}
          <div className="section">
            <p className="section-title"><FieldHelp id="ensight.fixes">Recommended Fixes</FieldHelp></p>
            {insight.fixesDetails && insight.fixesDetails.length > 0 ? (
              <div className="fix-cards-list">
                {insight.fixesDetails.map((fix, idx) => {
                  const typeClass = fix.type ? `fix-type-${fix.type.toLowerCase()}` : "fix-type-default";
                  const hasRange = fix.lowEstimate != null && fix.highEstimate != null && fix.lowEstimate !== fix.highEstimate;
                  return (
                    <div key={idx} className="fix-item-card">
                      <div className="fix-card-header">
                        <div className="fix-name-row">
                          <Icon name="check" className="tick-icon" size={15} />
                          <span className="fix-name">{fix.name}</span>
                        </div>
                        {fix.type && (
                          <span className={`fix-type-badge ${typeClass}`}>
                            <FieldHelp id="ensight.fixType">{fix.type}</FieldHelp>
                          </span>
                        )}
                      </div>

                      <div className="fix-cost-row">
                        <span><FieldHelp id="ensight.fixEstCost">Est. Cost:</FieldHelp> <strong className="fix-est-value">PKR {fmtPkr(fix.defaultEstimate)}</strong></span>
                        {hasRange && (
                          <span className="fix-range">
                            (Range: PKR {fmtPkr(fix.lowEstimate)} – {fmtPkr(fix.highEstimate)})
                          </span>
                        )}
                      </div>

                      {(fix.costBasis || fix.costingNote) && (
                        <div className="fix-meta-row">
                          {fix.costBasis && <span><strong><FieldHelp id="ensight.fixBasis">Basis:</FieldHelp></strong> {fix.costBasis}</span>}
                          {fix.costBasis && fix.costingNote && <span style={{ margin: "0 6px", color: "#cbd5e1" }}>•</span>}
                          {fix.costingNote && <span><strong><FieldHelp id="ensight.fixNote">Note:</FieldHelp></strong> {fix.costingNote}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <ul className="bullet-list">
                {insight.fixes?.map((f, i) => (
                  <li key={i} className="bullet-item">
                    <Icon name="check" className="tick-icon" size={14} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {insight.occurrences?.length > 1 && (
            <div className="section">
              <p className="section-title"><FieldHelp id="ensight.occurrences">Occurrence Windows</FieldHelp></p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                {insight.occurrences.map((o, i) => (
                  <span key={i} className="ghost-btn" style={{ height: "auto", padding: "4px 8px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <Icon name="clock" size={12} /> {o}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders whatever insights it is handed. All fetching, filtering and totalling now
 * happens once in EnSightTab against the single `/ensight/ensight_data` response, so
 * this component is purely presentational and works for one category or all eight.
 */
export default function InsightsTable({ insights, summary, loading, error, emptyTitle, emptyDesc }) {
  const navigate = useNavigate();

  // Insights only report a machine name, so the deep link falls back to
  // name matching in Node Settings (see utils/nodeSettingsLink.js).
  const goAssignLossParams = (insight) => {
    navigate(
      buildNodeSettingsLink({
        machineName: insight?.machine,
        focus: FOCUS_LOSS_PARAMS,
      }),
    );
  };
  const [modalId, setModalId] = useState(null);

  const activeModalInsight = useMemo(
    () => insights.find((i) => i.id === modalId) || null,
    [insights, modalId]
  );

  if (loading) {
    return (
      <div className="panel" style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#64748b", fontWeight: 600 }}>Loading enSight Insights...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">{error}</p>
          <p className="empty-desc">Check backend connection and press Apply to retry</p>
        </div>
      </div>
    );
  }

  if (!insights.length) {
    return (
      <div className="panel empty-state">
        <div>
          <div className="empty-icon"><Icon name="info" size={28} /></div>
          <p className="empty-title">{emptyTitle}</p>
          <p className="empty-desc">{emptyDesc}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="panel table-wrap">
        <div className="table-head">
          <p className="table-title">{insights.length} insight{insights.length !== 1 ? "s" : ""} detected</p>
          <p className="table-sub"><FieldHelp id="ensight.sortOrder">Sorted: Critical first</FieldHelp></p>
        </div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th><FieldHelp id="ensight.insight">Insight</FieldHelp></th>
                <th><FieldHelp id="ensight.machineCol">Machine</FieldHelp></th>
                <th><FieldHelp id="ensight.categoryCol">Category</FieldHelp></th>
                <th><FieldHelp id="ensight.severityCol">Severity</FieldHelp></th>
                <th><FieldHelp id="ensight.detected">Detected</FieldHelp></th>
                <th style={{ textAlign: "right" }}><FieldHelp id="ensight.loss">Loss (PKR)</FieldHelp></th>
                <th style={{ textAlign: "right" }}><FieldHelp id="ensight.fixCost">Est. Fix Cost (PKR)</FieldHelp></th>
                <th style={{ textAlign: "center" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {insights.map((row) => (
                <tr key={row.id}>
                  <td style={{ boxShadow: `inset 3px 0 0 ${row.severity === "Critical" ? "#ef4444" : "#f59e0b"}` }}>
                    <FieldHelp id={INSIGHT_HELP_ID[row.name]}>{row.name}</FieldHelp>
                  </td>
                  <td>{row.machine}</td>
                  <td><CategoryPill insight={row} /></td>
                  <td><SeverityPill severity={row.severity} /></td>
                  <td style={{ color: "#64748b", fontSize: 12 }}>{row.detected}</td>
                  <td className="numeric">
                    {row.loss > 0 ? fmtPkr(row.loss) : <span className="muted-cell">—</span>}
                  </td>
                  <td className="numeric">
                    {row.remedyCost > 0 ? (
                      <div>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>{fmtPkr(row.remedyCost)}</span>
                        {row.remedyCostLow && row.remedyCostHigh && (row.remedyCostLow !== row.remedyCostHigh) ? (
                          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                            {fmtPkr(row.remedyCostLow)} – {fmtPkr(row.remedyCostHigh)}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="muted-cell">—</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button className="action-btn" onClick={() => setModalId(row.id)}>
                      <Icon name="eye" size={14} />
                      <span style={{ marginLeft: 6 }}>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="totals">
                <td colSpan={5} style={{ fontSize: 12, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <FieldHelp id="ensight.totals">Totals ({insights.length} rows)</FieldHelp>
                </td>
                <td className="numeric" style={{ fontWeight: 900, color: "#334155" }}>{fmtPkr(summary.totalLoss)}</td>
                <td className="numeric" style={{ fontWeight: 900, color: "#0f766e" }}>
                  {summary.totalRemedyCost > 0 ? fmtPkr(summary.totalRemedyCost) : <span className="muted-cell">—</span>}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {modalId && (
        <InsightModal
          insight={activeModalInsight}
          onClose={() => setModalId(null)}
          onAssignLossParams={goAssignLossParams}
        />
      )}
    </>
  );
}
